import React, { useContext, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { AuthContext } from "../../../config/AuthContext";
import { Content } from "antd/es/layout/layout";
import Login from "../../Login";
import { Button, Card, Cascader, Col, Form, Input, Row, Modal, Radio, Select, ColorPicker, Tag, Tooltip, Badge, Divider, Popover } from "antd";
import { GetAlumneId } from "../../../dao/GetAlumneId";
import { PutUsuari } from "../../../dao/PutUsuari";
import { PostGenerateAvatar } from "../../../dao/jutge/PostGenerateAvatar";
import HomeColors from "../../../styles/HomeColors";
import { AlumneInfo } from "../../../model/AlumneInfo";
import { TrophyOutlined, TrophyFilled, StarFilled, ShoppingOutlined, UserOutlined, EditOutlined, CrownOutlined, ThunderboltOutlined, CheckCircleFilled, LockFilled, WarningOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { GetUsuarioTrofeos, GetSkins, GetUserUnlockedAvatars, GetUserEquipment, PostUserEquipment, getJavamonImage, GetSceneActivities } from "../../../dao/jutge/GetJavamonConfig";

interface Point {
    id: number;
    punts: number;
    descripcio: string;
    tipo?: string;
}

interface PerfilProps {
    user_id_simulado?: string;
    rpgOnly?: boolean;
}

const Perfil: React.FC<PerfilProps> = ({ user_id_simulado, rpgOnly = false }) => {
    const [form] = Form.useForm();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const isRpgOnly = rpgOnly || queryParams.get("rpgOnly") === "true";

    // Si está en modo RPG-only (ventana de juego) desde ruta directa, verificar iframe excepto si es un modal/simulado
    useEffect(() => {
        if (isRpgOnly && !user_id_simulado) {
            const isInsideIframe = window.self !== window.top;
            if (!isInsideIframe) {
                navigate("/home", { replace: true });
            }
        }
    }, [isRpgOnly, user_id_simulado, navigate]);

    const user_id_simulado_from_state = location.state?.user_id_simulado;
    const { isLoggedIn, userId, user, alumne, setAlumneInfo, setBreadCrumb, userPermisos } = useContext(AuthContext);
    const [selectedImage, setSelectedImage] = useState("");
    const [loading, setLoading] = useState(false);
    const [enviado, setEnviado] = useState(false);
    const [aluPoints, setAluPoints] = useState<Point[]>([]);
    const [misTrofeos, setMisTrofeos] = useState<any[]>([]);
    const [dbAvatars, setDbAvatars] = useState<any[]>([]);
    const [hasActiveRpg, setHasActiveRpg] = useState<boolean>(isRpgOnly);

    const aulesAlumne = (alumne as any)?.aules || (alumne as any)?.alumne?.aules || [];
    const hasJutgeContent = Array.isArray(aulesAlumne) && aulesAlumne.some((a: any) => Array.isArray(a.jutge) && a.jutge.length > 0);
    const isAlumno = user?.tipus === "2";
    const hasJutgePermission = userPermisos?.some((valor: any) => valor.accio === '/jutge') || hasJutgeContent || hasActiveRpg || isAlumno;

    const [isAvatarModalVisible, setIsAvatarModalVisible] = useState(false);
    const [avatarPrompt, setAvatarPrompt] = useState("");
    const [avatarGender, setAvatarGender] = useState("man");

    const [avatarBgId, setAvatarBgId] = useState("white");
    const [avatarHairId, setAvatarHairId] = useState("brown");
    const [avatarSkinId, setAvatarSkinId] = useState("fair");
    const [generatedAvatar, setGeneratedAvatar] = useState("");
    const [generating, setGenerating] = useState(false);

    const [hasAvatarGeneratorItem, setHasAvatarGeneratorItem] = useState(false);
    const [canRetry, setCanRetry] = useState(true);
    const [itemConsumed, setItemConsumed] = useState(false);

    const openAvatarModal = () => {
        setIsAvatarModalVisible(true);
        setGeneratedAvatar("");
        setAvatarPrompt("");
        setAvatarGender("man");
        setAvatarBgId("white");
        setAvatarHairId("brown");
        setAvatarSkinId("fair");
        setCanRetry(true);
        setItemConsumed(false);
    }

    const handleGenerateAvatar = async (isRetry = false) => {
        setGenerating(true);
        try {
            const bgOpt = BACKGROUND_COLORS.find(o => o.id === avatarBgId);
            const hairOpt = HAIR_COLORS.find(o => o.id === avatarHairId);
            const skinOpt = SKIN_OPTIONS.find(o => o.id === avatarSkinId);

            let genderTerm = 'Man';
            if (avatarGender === 'woman') genderTerm = 'Woman';
            else if (avatarGender === 'non-binary') genderTerm = 'Androgynous person';

            const bgTerm = bgOpt ? bgOpt.prompt : 'white background';
            const hairTerm = hairOpt ? hairOpt.prompt : 'brown hair';
            const skinTerm = skinOpt ? skinOpt.prompt : 'fair skin';

            const finalPrompt = `${genderTerm}, ${bgTerm}, ${hairTerm}, ${skinTerm}. Accessories/Details: ${avatarPrompt}`;

            const res = await PostGenerateAvatar(userId, finalPrompt);
            if (res.ok && res.data) {
                const avatarUrl = res.data;

                if (!itemConsumed) {
                    const importRes = await import("../../../dao/shop/PutConsumeObject");
                    const consumed = await importRes.PutConsumeObject(userId, 7);

                    if (consumed) {
                        setItemConsumed(true);
                        setGeneratedAvatar(avatarUrl);
                        loadData();
                    } else {
                        console.error("Error al consumir el objeto Generar Avatar");
                    }
                } else {
                    setGeneratedAvatar(avatarUrl);
                }

                if (isRetry) {
                    setCanRetry(false);
                }
            } else {
                if (res.error && (res.error.includes("blocked") || res.error.includes("policy") || res.error.includes("Policy"))) {
                    alert("No se ha podido generar el avatar por violación de políticas de contenido de IA. Por favor, revisa los términos o prueba con otros accesorios.");
                } else {
                    console.error("Error avatar generation:", res.error);
                    alert("Error al generar el avatar. Inténtalo de nuevo más tarde.");
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setGenerating(false);
        }
    }

    const idUsuario = user_id_simulado || user_id_simulado_from_state || userId;

    const [cursos, setCursos] = useState<any[]>([]);

    useEffect(() => {
        if (isLoggedIn) {
            loadData();
            loadCursos();
            loadDbAvatars();
            setBreadCrumb(
                [
                    { nom: "Home", nav: "/home" },
                    { nom: "Perfil", nav: "/perfil" },
                ],
                true
            );
        }
        // Nota: no dependemos de 'selectedImage' para no recargar (y pisar la
        // selección) cada vez que el usuario elige un avatar distinto.
    }, [idUsuario]);

    const loadCursos = async () => {
        try {
            const { GetCursos } = await import("../../../dao/metadata/ManageCursos");
            const data = await GetCursos();
            const builtCursos = buildCascaderOptions(data);
            setCursos(builtCursos);
        } catch (e) {
            console.error("Error loading cursos", e);
        }
    }

    const buildCascaderOptions = (flatList: any[]) => {
        const roots: any[] = [];
        const map: any = {};

        flatList.forEach(item => {
            map[item.id] = {
                value: item.value,
                label: item.label,
                children: []
            };
        });

        flatList.forEach(item => {
            if (item.parent_id) {
                if (map[item.parent_id]) {
                    map[item.parent_id].children.push(map[item.id]);
                }
            } else {
                roots.push(map[item.id]);
            }
        });

        const cleanChildren = (nodes: any[]) => {
            nodes.forEach(node => {
                if (node.children.length === 0) {
                    delete node.children;
                } else {
                    cleanChildren(node.children);
                }
            });
        };
        cleanChildren(roots);

        return roots;
    };

    const [customAvatars, setCustomAvatars] = useState<string[]>([]);
    const [unlockedAvatarIds, setUnlockedAvatarIds] = useState<number[]>([]);

    // === RPG Equipment ===
    const [allSkins, setAllSkins] = useState<any>({ skins: [], armes: [], escuts: [], arcs: [], flechas: [], magias: [] });
    const [userEquipment, setUserEquipment] = useState<any>(null);
    const [savingEquipment, setSavingEquipment] = useState(false);

    const loadDbAvatars = async () => {
        try {
            const data = await GetSkins();
            const unlocked = await GetUserUnlockedAvatars(idUsuario);
            const equipment = await GetUserEquipment(idUsuario);
            if (unlocked) {
                setUnlockedAvatarIds(unlocked);
            }
            if (data) {
                setAllSkins({
                    skins: data.skins ? data.skins.map((x: any) => ({ ...x, _type: 'skin' })) : [],
                    armes: data.armes ? data.armes.map((x: any) => ({ ...x, _type: 'arma' })) : [],
                    escuts: data.escuts ? data.escuts.map((x: any) => ({ ...x, _type: 'escut' })) : [],
                    arcs: data.arcs ? data.arcs.map((x: any) => ({ ...x, _type: 'arc' })) : [],
                    flechas: data.flechas ? data.flechas.map((x: any) => ({ ...x, _type: 'flecha' })) : [],
                    magias: data.magias ? data.magias.map((x: any) => ({ ...x, _type: 'magia' })) : [],
                    avatars: data.avatars ? data.avatars.map((x: any) => ({ ...x, _type: 'avatar' })) : []
                });
                if (data.avatars) setDbAvatars(data.avatars.map((x: any) => ({ ...x, _type: 'avatar' })).filter((a: any) => a.active === 1));
            }
            if (equipment) {
                setUserEquipment(equipment);
            }
            // Si el avatar guardado apunta a un avatar del juego, marcar el id para que se muestre seleccionado
            const currentAv = alumne?.alumne?.avatar || '';
            const match = currentAv.match(/\/jutge\/avatar-content\/(\d+)/) || currentAv.match(/\/jutge\/game\/image\/avatar\/(\d+)/);
            if (match) {
                setSelectedDbAvatarId(Number(match[1]));
            } else if (equipment?.id_avatar) {
                setSelectedDbAvatarId(Number(equipment.id_avatar));
            } else if (!currentAv || currentAv === 'avatar1.jpg' || currentAv === '/images/avatar1.jpg') {
                setSelectedDbAvatarId(1);
            }
        } catch (e) {
            console.error("Error loading avatars from DB", e);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            if (!selectedImage && alumne?.alumne) {
                let av = alumne.alumne.avatar;
                if (av === 'avatar1.jpg') av = '/images/avatar1.jpg';
                setSelectedImage(av || '/images/avatar1.jpg');
            }
            const alu: any = await GetAlumneId(idUsuario);
            if (idUsuario === userId) {
                setAlumneInfo(alu);
            }
            setAluPoints(alu.points ?? []);

            // Cargar avatares personalizados (IA)
            const customRes: any = await import("../../../dao/jutge/GetCustomAvatars").then(m => m.GetCustomAvatars(idUsuario));
            if (customRes.ok && customRes.data) {
                setCustomAvatars(customRes.data);
            }

            // Cargar trofeos
            const trofeosData = await GetUsuarioTrofeos(idUsuario);
            if (trofeosData) {
                setMisTrofeos(trofeosData);
            }

            console.log(alu);
            // Verificar si tiene el objeto "Generar Avatar" (id 7)
            if (alu.objectes && Array.isArray(alu.objectes)) {
                const found = alu.objectes.find((o: any) => o.id === 7 && o.quantitat > 0);
                setHasAvatarGeneratorItem(!!found);
            }

            // Comprobar si el usuario tiene activado el RPG en alguna de sus aulas
            if (isRpgOnly) {
                setHasActiveRpg(true);
            } else {
                try {
                    const activities = await GetSceneActivities();
                    if (activities && activities.length > 0) {
                        const uniqueAulas = Array.from(new Set(activities.map((a: any) => a.id_aula)));
                        const userAulas = alu?.aules || [];
                        const activeRpg = userAulas.some((aula: any) =>
                            uniqueAulas.includes(aula.id) && (aula.jocHabilitat === undefined || aula.jocHabilitat === 1 || aula.jocHabilitat === true)
                        );
                        setHasActiveRpg(activeRpg);
                    } else {
                        setHasActiveRpg(false);
                    }
                } catch (e) {
                    console.error("Error checking active RPG classrooms in Perfil", e);
                }
            }

            form.setFieldsValue({
                ...alu.alumne,
                curs: [alu.alumne.grup, alu.alumne.curs],
            });
        } catch (err) {
            console.error("Error al cargar datos de alumno:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEquipItem = async (type: 'skin' | 'arma' | 'escut' | 'arc' | 'flecha' | 'magia' | 'avatar', id: number | null) => {
        setSavingEquipment(true);
        try {
            // Solo enviamos el equipamiento equipado, NUNCA los unlocked_*
            // El backend solo toca las tablas de desbloqueados si el campo está presente en el body
            const body = {
                id_usuari: Number(idUsuario),
                id_skin:  type === 'skin'  ? id : (userEquipment?.id_skin  ?? null),
                id_arma:  type === 'arma'  ? id : (userEquipment?.id_arma  ?? null),
                id_escut: type === 'escut' ? id : (userEquipment?.id_escut ?? null),
                id_arc:   type === 'arc'   ? id : (userEquipment?.id_arc   ?? null),
                id_flecha: type === 'flecha' ? id : (userEquipment?.id_flecha ?? null),
                id_magia: type === 'magia' ? id : (userEquipment?.id_magia ?? null),
                id_avatar: type === 'avatar' ? id : (userEquipment?.id_avatar ?? null),
            };
            await PostUserEquipment(body);
            const updated = await GetUserEquipment(idUsuario);
            if (updated) setUserEquipment(updated);

            if (window.self !== window.top) {
                window.parent.postMessage({ type: "REFRESH_PLAYER_EQUIPMENT" }, "*");
            }
        } catch (e) {
            console.error("Error saving equipment", e);
        }
        setSavingEquipment(false);
    };

    // Obtener la imagen del avatar por defecto (ID 1)
    const getDefaultAvatarImage = () => {
        const defaultAvatar = dbAvatars.find((a: any) => a.id === 1);
        if (defaultAvatar) {
            return getJavamonImage(defaultAvatar) || '/images/avatar1.jpg';
        }
        return '/images/avatar1.jpg';
    };

    const getAvatarImageSrc = (avatar: any) => {
        return getJavamonImage(avatar) || '/images/avatar1.jpg';
    };

    // Devuelve la URL pública que se guarda en bbdd para un avatar del juego
    const getDbAvatarUrl = (avatarId: number) => {
        const apiUrl = process.env.REACT_APP_API_URL;
        return `${apiUrl}:8061/jutge/game/image/avatar/${avatarId}`;
    };

    // Estado que distingue si el avatar seleccionado es del juego (id) o IA/custom (null)
    const [selectedDbAvatarId, setSelectedDbAvatarId] = useState<number | null>(null);

    const currentAvatar = (() => {
        let av = selectedImage;
        if (!av || av === 'avatar1.jpg' || av === '/images/avatar1.jpg') {
            if (selectedDbAvatarId) {
                const found = dbAvatars.find((a: any) => a.id === selectedDbAvatarId);
                if (found) return getAvatarImageSrc(found);
            }
            return getDefaultAvatarImage();
        }
        if (av.startsWith("http://localhost:") && window.location.hostname !== 'localhost') {
            av = av.replace(/http:\/\/localhost:\d+/, `${process.env.REACT_APP_API_URL}:8061`);
        }
        if (av.startsWith("http") || av.startsWith("data:") || av.startsWith("/")) return av;
        if (av.endsWith(".jpg") || av.endsWith(".png") || av.endsWith(".jpeg")) return `/images/${av}`;
        return av;
    })();

    // Al clicar avatar del juego: guarda el id para no subir como IA al guardar
    const handleDbAvatarClick = (avatar: any) => {
        const imgSrc = getAvatarImageSrc(avatar);
        setSelectedImage(imgSrc);
        setSelectedDbAvatarId(avatar.id);
    };

    // Al clicar avatar IA/custom: limpia el id del juego
    const handleImageClick = (img: string) => {
        setSelectedImage(img);
        setSelectedDbAvatarId(null);
    };

    const submitData = async () => {
        setLoading(true);
        setEnviado(true);
        try {
            let avatarPath: string;

            if (selectedDbAvatarId !== null) {
                // Avatar del juego: guardar la URL del endpoint, nunca subir el blob
                avatarPath = getDbAvatarUrl(selectedDbAvatarId);
                await handleEquipItem('avatar', selectedDbAvatarId);
            } else if (selectedImage.startsWith("data:")) {
                // Avatar IA real: subir el blob y guardar la URL resultante
                const uploadRes = await import("../../../dao/jutge/PostSaveAvatar").then(m => m.PostSaveAvatar(idUsuario, selectedImage));
                if (uploadRes.ok && uploadRes.data) {
                    avatarPath = uploadRes.data;
                } else {
                    console.error("Error saving avatar:", uploadRes.error);
                    avatarPath = selectedImage;
                }
            } else {
                // Ya es una URL (avatar guardado previamente, IA o juego)
                avatarPath = selectedImage;
            }

            const formData = form.getFieldsValue();
            const newFormData = {
                ...formData,
                id: idUsuario,
                avatar: avatarPath,
                curs: formData.curs[1],
                grup: formData.curs[0],
                repetidor: formData.repetidor === 0 ? false : formData.repetidor,
            };
            await PutUsuari(newFormData);

            // Reflejar de inmediato el avatar guardado en esta pantalla
            setSelectedImage(avatarPath);

            const updated = await GetAlumneId(idUsuario);
            // Forzar que el avatar del contexto sea exactamente el que acabamos de
            // guardar, para que el menú superior (que lee alumne.alumne.avatar) se
            // actualice a la primera, sin depender de cómo lo serialice el backend.
            const updatedWithAvatar = {
                ...updated,
                alumne: { ...updated?.alumne, avatar: avatarPath }
            };
            setAlumneInfo(updatedWithAvatar);
            setAluPoints(updated.points ?? []);

            if (window.self !== window.top) {
                window.parent.postMessage({ type: "REFRESH_PLAYER_EQUIPMENT" }, "*");
            }
        } catch (err) {
            console.error("Error al guardar datos:", err);
        } finally {
            setLoading(false);
            setEnviado(false);
        }
    };

    const totalPoints = aluPoints.reduce((acc, p) => acc + (p.punts ?? 0), 0);

    const getIcon = (color?: string) => {
        return <TrophyOutlined style={{ color: color, marginRight: 6 }} />;
    };

    if (!isLoggedIn) return <Login />;

    const BACKGROUND_COLORS = [
        { id: 'white', color: '#ffffff', label: 'Blanco', prompt: 'white background' },
        { id: 'grey', color: '#8c8c8c', label: 'Gris', prompt: 'grey background' },
        { id: 'red', color: '#ff4d4f', label: 'Rojo', prompt: 'red background' },
        { id: 'orange', color: '#ffa940', label: 'Naranja', prompt: 'orange background' },
        { id: 'yellow', color: '#ffec3d', label: 'Amarillo', prompt: 'yellow background' },
        { id: 'light_green', color: '#95de64', label: 'Verde Claro', prompt: 'light green background' },
        { id: 'dark_green', color: '#389e0d', label: 'Verde Oscuro', prompt: 'dark green background' },
        { id: 'blue', color: '#1890ff', label: 'Azul', prompt: 'blue background' },
        { id: 'purple', color: '#722ed1', label: 'Morado', prompt: 'purple background' },
        { id: 'pink', color: '#ffadd2', label: 'Rosa', prompt: 'pink background' },
    ];

    const HAIR_COLORS = [
        { id: 'brown', color: '#5d4037', label: 'Castaño', prompt: 'brown hair' },
        { id: 'blonde', color: '#f4d03f', label: 'Rubio', prompt: 'blonde hair' },
        { id: 'black', color: '#000000', label: 'Negro', prompt: 'black hair' },
        { id: 'red', color: '#d35400', label: 'Pelirrojo', prompt: 'ginger red hair' },
        { id: 'orange', color: '#e67e22', label: 'Naranja', prompt: 'orange hair' },
        { id: 'white', color: '#ecf0f1', label: 'Blanco', prompt: 'white hair' },
        { id: 'blue', color: '#2e86de', label: 'Azul', prompt: 'blue hair' },
        { id: 'green', color: '#2ecc71', label: 'Verde', prompt: 'green hair' },
        { id: 'pink', color: '#ff9ff3', label: 'Rosa', prompt: 'pink hair' },
        { id: 'purple', color: '#9b59b6', label: 'Morado', prompt: 'purple hair' },
    ];

    const SKIN_OPTIONS = [
        { id: 'pale', gradient: 'linear-gradient(to bottom, #ffe0bd, #ffcd94)', label: 'Pálida', prompt: 'pale skin' },
        { id: 'fair', gradient: 'linear-gradient(to bottom, #f1c27d, #e0ac69)', label: 'Clara', prompt: 'fair skin' },
        { id: 'medium', gradient: 'linear-gradient(to bottom, #c68642, #8d5524)', label: 'Media', prompt: 'medium skin tone, tan skin' },
        { id: 'dark', gradient: 'linear-gradient(to bottom, #6d4c41, #3e2723)', label: 'Oscura', prompt: 'dark skin' },
        { id: 'deep', gradient: 'linear-gradient(to bottom, #3e2723, #1a100e)', label: 'Muy Oscura', prompt: 'very dark skin' },
    ];

    const renderOptionSelector = (title: string, options: any[], selectedId: string, onSelect: (id: string) => void) => (
        <div style={{ marginBottom: 15 }}>
            <div style={{ marginBottom: 5, fontWeight: 500 }}>{title}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {options.map((opt) => {
                    const isSelected = selectedId === opt.id;
                    return (
                        <div
                            key={opt.id}
                            title={opt.label}
                            onClick={() => onSelect(opt.id)}
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: opt.color || opt.gradient,
                                cursor: 'pointer',
                                border: isSelected ? '3px solid #1890ff' : '1px solid #ddd',
                                boxShadow: isSelected ? '0 0 5px rgba(24,144,255,0.5)' : 'none',
                                transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                transition: 'all 0.2s ease'
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );

    const handleSaveAndClose = async () => {
        if (generatedAvatar) {
            try {
                if (generatedAvatar.startsWith("data:")) {
                    const res = await import("../../../dao/jutge/PostSaveAvatar").then(m => m.PostSaveAvatar(userId, generatedAvatar));
                    if (res && res.ok && res.data) {
                        setSelectedImage(res.data);
                    }
                }
                await loadData();
            } catch (error) {
                console.error("Error saving avatar:", error);
            }
        }
        setIsAvatarModalVisible(false);
    };

    // --- STYLES ---
    const gradientHeader = {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px 16px 0 0',
        padding: '32px 40px',
        color: '#fff',
        position: 'relative' as const,
        overflow: 'hidden' as const,
    };

    const avatarRingStyle = (isSelected: boolean) => ({
        width: 76,
        height: 76,
        borderRadius: '50%',
        cursor: 'pointer',
        objectFit: 'cover' as const,
        border: isSelected ? '3px solid #764ba2' : '3px solid transparent',
        boxShadow: isSelected ? '0 0 0 3px rgba(118,75,162,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
        filter: isSelected ? 'none' : 'brightness(0.92)',
        transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
    });

    const sectionCardStyle = {
        borderRadius: 16,
        border: 'none',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        marginBottom: 20,
        overflow: 'hidden' as const,
    };

    const renderRpgEquipment = () => {
        if (!hasActiveRpg || !userEquipment) return null;
        const parseIds = (s: string | null | undefined): number[] =>
            s ? s.split(',').map((x: string) => parseInt(x, 10)).filter(Boolean) : [];

        const unlockedSkins  = parseIds(userEquipment.unlocked_skins);
        const unlockedArmes  = parseIds(userEquipment.unlocked_armes);
        const unlockedEscuts = parseIds(userEquipment.unlocked_escuts);
        const unlockedArcs   = parseIds(userEquipment.unlocked_arcs);
        const unlockedFlechas = parseIds(userEquipment.unlocked_flechas);
        const unlockedMagias  = parseIds(userEquipment.unlocked_magias);

        const hasAnyEquipment = unlockedSkins.length > 0 || unlockedArmes.length > 0 ||
            unlockedEscuts.length > 0 || unlockedArcs.length > 0 || unlockedFlechas.length > 0 || unlockedMagias.length > 0;
        if (!hasAnyEquipment && !isRpgOnly) return null;

        const equippedSkin  = allSkins.skins?.find((s: any) => s.id === userEquipment.id_skin);
        const equippedArma  = allSkins.armes?.find((s: any) => s.id === userEquipment.id_arma);
        const equippedEscut = allSkins.escuts?.find((s: any) => s.id === userEquipment.id_escut);
        const equippedArc   = allSkins.arcs?.find((s: any) => s.id === userEquipment.id_arc);
        const equippedFlecha = allSkins.flechas?.find((s: any) => s.id === userEquipment.id_flecha);
        const equippedMagia  = allSkins.magias?.find((s: any) => s.id === userEquipment.id_magia);

        const charClass = equippedSkin?.type || 'Aventurero';
        const maxHealth = equippedSkin?.salut || 0;
        const speed = equippedSkin?.velocitat || 0;
        let defense = equippedSkin?.defensa || 0;
        let damage = 0;
        let critico = 0;
        let velocidadAtaque = 5;
        let range = 0;
        let bloqueo = 0;
        let regeneracion = 0;
        
        if (charClass === 'Guerrero') {
            damage = equippedArma?.dany || 0;
            critico = equippedArma?.critico || 0;
            velocidadAtaque = equippedArma?.velocidad !== undefined ? equippedArma.velocidad : 5;
            range = equippedArma?.rang || 0;
            defense += equippedEscut?.defensa || 0;
            bloqueo = equippedEscut?.bloqueo || 0;
            regeneracion = equippedEscut?.regeneracion || 0;
        } else if (charClass === 'Arquero') {
            if (equippedArc) {
                damage = (equippedArc.dany || 0) + (equippedFlecha?.dany || 0);
                range = (equippedArc.rang || 0) + (equippedFlecha?.rang || 0);
                velocidadAtaque = equippedArc?.velocidad !== undefined ? equippedArc.velocidad : 5;
                critico = equippedFlecha?.critico || 0;
            }
        } else if (charClass === 'Mago') {
            damage = equippedArma?.dany || 0;
            critico = equippedArma?.critico || 0;
            velocidadAtaque = equippedArma?.velocidad !== undefined ? equippedArma.velocidad : 5;
            range = equippedArma?.rang || 0;
            const isStavesEquipped = equippedArma?.ident?.startsWith('Staves');
            if (isStavesEquipped && equippedMagia) {
                damage += equippedMagia.dany || 0;
                range += equippedMagia.rang || 0;
            }
        } else if (charClass === 'Luchador') {
            if (equippedMagia) {
                damage = equippedMagia.dany || 0;
                range = equippedMagia.rang || 0;
            }
            velocidadAtaque = 5;
        }

        const statsBlock = (
            <div style={{ background: '#fafafa', padding: 24, borderRadius: 12, border: '1px solid #eaeaea', height: '100%', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}>
                <h4 style={{ margin: '0 0 20px', color: '#764ba2', fontWeight: 700, fontSize: 16 }}>Atributos</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14 }}>
                    <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Clase:</span> <Tag color={charClass === 'Guerrero' ? 'red' : charClass === 'Arquero' ? 'green' : charClass === 'Mago' ? 'blue' : charClass === 'Luchador' ? 'orange' : 'default'} style={{float: 'right', margin: 0}}>{charClass}</Tag></div>
                    <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Salud Máx:</span> <b style={{float: 'right', color: '#ff4d4f'}}>{maxHealth} ❤️</b></div>
                    <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Velocidad:</span> <b style={{float: 'right', color: '#1890ff'}}>{speed} ⚡</b></div>
                    <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Defensa:</span> <b style={{float: 'right', color: '#52c41a'}}>{defense} 🛡️</b></div>
                    <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Daño:</span> <b style={{float: 'right', color: '#faad14'}}>{damage} ⚔️</b></div>
                    {(charClass === 'Guerrero' || charClass === 'Mago' || charClass === 'Arquero' || charClass === 'Luchador') && (
                        <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Crítico:</span> <b style={{float: 'right', color: '#f5222d'}}>{critico}% 🎯</b></div>
                    )}
                    {(charClass === 'Guerrero' || charClass === 'Mago' || charClass === 'Arquero' || charClass === 'Luchador') && (
                        <div style={{ borderBottom: charClass === 'Arquero' ? 'none' : '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Vel. Ataque:</span> <b style={{float: 'right', color: '#13c2c2'}}>{velocidadAtaque} 🌪️</b></div>
                    )}
                    {charClass === 'Guerrero' && (
                        <>
                            <div style={{ borderBottom: '1px dashed #e0e0e0', paddingBottom: 8 }}><span style={{color: '#888'}}>Bloqueo:</span> <b style={{float: 'right', color: '#36cfc9'}}>{bloqueo}% 🛡️</b></div>
                            <div style={{ borderBottom: 'none', paddingBottom: 8 }}><span style={{color: '#888'}}>Regen.:</span> <b style={{float: 'right', color: '#73d13d'}}>{regeneracion} ❤️‍🩹</b></div>
                        </>
                    )}
                    {charClass !== 'Guerrero' && charClass !== 'Aventurero' && (
                        <div><span style={{color: '#888'}}>Alcance:</span> <b style={{float: 'right', color: '#722ed1'}}>{range} 📏</b></div>
                    )}
                </div>
            </div>
        );

        const slotStyle = (equipped: boolean): React.CSSProperties => ({
            width: 72,
            height: 72,
            border: equipped ? '2px solid #764ba2' : '2px dashed #d9d9d9',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: equipped ? 'rgba(118,75,162,0.06)' : '#fafafa',
            cursor: 'pointer',
            transition: 'all 0.2s',
            position: 'relative',
            flexShrink: 0,
        });

        const slotImg = (item: any) => item
            ? <img src={getJavamonImage(item)} alt={item.nom} style={{ maxWidth: 56, maxHeight: 56, objectFit: 'contain' }} />
            : <span style={{ fontSize: 28, filter: 'grayscale(1)', opacity: 0.3 }}>?</span>;

        const skinImg = equippedSkin
            ? <img src={getJavamonImage(equippedSkin)} alt={equippedSkin.nom}
                   style={{ width: 110, height: 110, objectFit: 'contain' }} />
            : <span style={{ fontSize: 60, filter: 'grayscale(1)', opacity: 0.2 }}>🧍</span>;

        const ItemPicker = ({ type, label, icon, items, equippedId, disabled, disabledMessage, groupByEvolution }: {
            type: 'skin' | 'arma' | 'escut' | 'arc' | 'flecha' | 'magia';
            label: string;
            icon: string;
            items: any[];
            equippedId: number | null;
            disabled?: boolean;
            disabledMessage?: string;
            groupByEvolution?: boolean;
        }) => {
            let renderContent = null;

            const renderItem = (item: any) => {
                const isEq = item.id === equippedId;
                const statsContent = (
                    <div style={{ fontSize: 12, minWidth: 120 }}>
                        {item.type && <div><span style={{color: '#888'}}>Clase:</span> <b style={{float: 'right'}}>{item.type}</b></div>}
                        {item.tipo && <div><span style={{color: '#888'}}>Tipo:</span> <b style={{float: 'right', textTransform: 'capitalize'}}>{item.tipo}</b></div>}
                        {item.es_escudo && <div><span style={{color: '#888'}}>Clasificación:</span> <b style={{float: 'right', textTransform: 'capitalize'}}>{item.es_escudo === 'escudo' || item.es_escudo === 1 || item.es_escudo === true ? 'Escudo' : item.es_escudo === 'golpe' ? 'Golpe' : 'Magia'}</b></div>}
                        {item.salut !== undefined && item.salut !== null && <div><span style={{color: '#888'}}>Salud:</span> <b style={{float: 'right'}}>{item.salut}</b></div>}
                        {item.velocitat !== undefined && item.velocitat !== null && <div><span style={{color: '#888'}}>Velocidad:</span> <b style={{float: 'right'}}>{item.velocitat}</b></div>}
                        {item.defensa !== undefined && item.defensa !== null && <div><span style={{color: '#888'}}>Defensa:</span> <b style={{float: 'right'}}>{item.defensa}</b></div>}
                        {item.dany !== undefined && item.dany !== null && <div><span style={{color: '#888'}}>Daño:</span> <b style={{float: 'right'}}>{item.dany}</b></div>}
                        {item.critico !== undefined && item.critico !== null && <div><span style={{color: '#888'}}>Crítico:</span> <b style={{float: 'right'}}>{item.critico}%</b></div>}
                        {item.velocidad !== undefined && item.velocidad !== null && <div><span style={{color: '#888'}}>Vel. Ataque:</span> <b style={{float: 'right'}}>{item.velocidad}</b></div>}
                        {item.rang !== undefined && item.rang !== null && <div><span style={{color: '#888'}}>Alcance:</span> <b style={{float: 'right'}}>{item.rang}</b></div>}
                    </div>
                );
                return (
                    <Popover key={item.id} title={disabled ? disabledMessage : item.nom} content={disabled ? null : statsContent} trigger="hover" placement="right">
                        <div
                            onClick={() => !disabled && !savingEquipment && handleEquipItem(type, isEq ? null : item.id)}
                            style={{
                                ...slotStyle(isEq),
                                boxShadow: isEq ? '0 0 0 3px rgba(118,75,162,0.3)' : 'none',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {slotImg(item)}
                            {isEq && <CheckCircleFilled style={{
                                position: 'absolute', top: -6, right: -6,
                                color: '#764ba2', fontSize: 16, background: '#fff', borderRadius: '50%'
                            }} />}
                        </div>
                    </Popover>
                );
            };

            if (groupByEvolution) {
                const itemMap = new Map();
                const isTarget = new Set();
                
                items.forEach(item => {
                    itemMap.set(item.ident, item);
                    if (item.evoluciona_a && item.evoluciona_a !== 'no evoluciona') {
                        isTarget.add(item.evoluciona_a);
                    }
                });

                const chains: any[][] = [];
                items.forEach(item => {
                    if (!isTarget.has(item.ident)) {
                        const chain = [];
                        let curr = item;
                        let safe = 0;
                        while (curr && safe < 5) {
                            chain.push(curr);
                            if (curr.evoluciona_a && curr.evoluciona_a !== 'no evoluciona' && itemMap.has(curr.evoluciona_a)) {
                                curr = itemMap.get(curr.evoluciona_a);
                            } else {
                                curr = null;
                            }
                            safe++;
                        }
                        chains.push(chain);
                    }
                });

                renderContent = (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', opacity: disabled ? 0.5 : 1 }}>
                        {chains.map((chain, i) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                                {chain.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                        {index > 0 && <span style={{ color: '#ccc', fontSize: 12 }}>→</span>}
                                        {renderItem(item)}
                                    </React.Fragment>
                                ))}
                            </div>
                        ))}
                    </div>
                );
            } else {
                renderContent = (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', opacity: disabled ? 0.5 : 1 }}>
                        {items.map(renderItem)}
                    </div>
                );
            }

            return (
                <div>
                    <div style={{ fontSize: 11, color: '#999', textAlign: 'center', marginBottom: 4 }}>
                        {label} {disabled && <Tooltip title={disabledMessage}><WarningOutlined style={{ color: '#faad14', marginLeft: 4 }} /></Tooltip>}
                    </div>
                    {renderContent}
                </div>
            );
        };

        return (
            <Card style={{ ...sectionCardStyle, margin: isRpgOnly ? '0 auto' : undefined }} bodyStyle={{ padding: '24px 32px' }}>
                <h3 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: '#333' }}>
                    <span style={{ marginRight: 8 }}>⚔️</span>
                    Equipamiento RPG
                    <span style={{ fontSize: 13, fontWeight: 400, color: '#999', marginLeft: 12 }}>
                        Haz clic en un item para equiparlo/desequiparlo
                    </span>
                </h3>

                <Row gutter={[24, 24]}>
                    <Col xs={24} md={6} lg={5}>
                        {statsBlock}
                    </Col>
                    <Col xs={24} md={18} lg={19}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>

                    {/* Magia */}
                    {(equippedSkin?.type === 'Mago' || equippedSkin?.type === 'Luchador') && unlockedMagias.length > 0 && (() => {
                        const isStavesEquipped = equippedArma?.ident?.startsWith('Staves');
                        
                        const myMagias = allSkins.magias?.filter((m: any) => {
                            if (m.active !== 1 || !unlockedMagias.includes(m.id)) return false;
                            if (equippedSkin?.type === 'Luchador' && m.es_escudo !== 'golpe') return false;
                            if (equippedSkin?.type === 'Mago' && m.es_escudo === 'golpe') return false;
                            return true;
                        }) || [];
                        const byElement: Record<string, any[]> = {};
                        myMagias.forEach((m: any) => {
                            const el = m.elemento || 'Ninguno';
                            if (!byElement[el]) byElement[el] = [];
                            byElement[el].push(m);
                        });

                        return (
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {Object.entries(byElement).map(([el, items]) => (
                                    <ItemPicker
                                        key={el}
                                        type="magia"
                                        label={`✨ Magia (${el})`}
                                        icon="✨"
                                        items={items}
                                        equippedId={userEquipment.id_magia}
                                        disabled={!isStavesEquipped}
                                        disabledMessage={!isStavesEquipped ? "Equipa un arma de tipo 'Staves' para usar magia" : undefined}
                                    />
                                ))}
                            </div>
                        );
                    })()}

                    {/* Escudo */}
                    {equippedSkin?.type === 'Guerrero' && unlockedEscuts.length > 0 && (
                        <ItemPicker
                            type="escut" label="🛡️ Escudo" icon="🛡️"
                            items={allSkins.escuts?.filter((e: any) => e.active === 1 && unlockedEscuts.includes(e.id)) || []}
                            equippedId={userEquipment.id_escut}
                            groupByEvolution={true}
                        />
                    )}

                    {/* Skin Central */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 140, height: 140,
                            border: '3px solid #764ba2',
                            borderRadius: 20,
                            background: 'linear-gradient(135deg, #f9f0ff, #efdbff)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 20px rgba(118,75,162,0.15)',
                            position: 'relative',
                        }}>
                            {skinImg}
                            {equippedSkin && <div style={{
                                position: 'absolute', bottom: 4, left: 0, right: 0,
                                textAlign: 'center', fontSize: 11, color: '#764ba2', fontWeight: 600
                            }}>{equippedSkin.nom}</div>}
                        </div>

                        {unlockedSkins.length > 0 && (
                            <div>
                                <div style={{ fontSize: 11, color: '#999', textAlign: 'center', marginBottom: 4 }}>👕 Skin</div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 220 }}>
                                    {allSkins.skins
                                        ?.filter((s: any) => s.active === 1 && unlockedSkins.includes(s.id))
                                        .map((item: any) => {
                                        const isEq = item.id === userEquipment.id_skin;
                                        const statsContent = (
                                            <div style={{ fontSize: 12, minWidth: 120 }}>
                                                {item.type && <div><span style={{color: '#888'}}>Clase:</span> <b style={{float: 'right'}}>{item.type}</b></div>}
                                                {item.salut !== undefined && item.salut !== null && <div><span style={{color: '#888'}}>Salud:</span> <b style={{float: 'right'}}>{item.salut}</b></div>}
                                                {item.velocitat !== undefined && item.velocitat !== null && <div><span style={{color: '#888'}}>Velocidad:</span> <b style={{float: 'right'}}>{item.velocitat}</b></div>}
                                                {item.defensa !== undefined && item.defensa !== null && <div><span style={{color: '#888'}}>Defensa:</span> <b style={{float: 'right'}}>{item.defensa}</b></div>}
                                                {item.bloqueo !== undefined && item.bloqueo !== null && <div><span style={{color: '#888'}}>Bloqueo %:</span> <b style={{float: 'right'}}>{item.bloqueo}</b></div>}
                                                {item.regeneracion !== undefined && item.regeneracion !== null && <div><span style={{color: '#888'}}>Regen.:</span> <b style={{float: 'right'}}>{item.regeneracion}</b></div>}
                                                {item.dany !== undefined && item.dany !== null && <div><span style={{color: '#888'}}>Daño:</span> <b style={{float: 'right'}}>{item.dany}</b></div>}
                                            </div>
                                        );
                                        return (
                                            <Popover key={item.id} title={item.nom} content={statsContent} trigger="hover" placement="bottom">
                                                <div
                                                    onClick={() => !savingEquipment && handleEquipItem('skin', isEq ? null : item.id)}
                                                    style={{
                                                        ...slotStyle(isEq),
                                                        width: 56, height: 56,
                                                        boxShadow: isEq ? '0 0 0 3px rgba(118,75,162,0.3)' : 'none',
                                                    }}
                                                >
                                                    {slotImg(item)}
                                                    {isEq && <CheckCircleFilled style={{
                                                        position: 'absolute', top: -6, right: -6,
                                                        color: '#764ba2', fontSize: 14, background: '#fff', borderRadius: '50%'
                                                    }} />}
                                                </div>
                                            </Popover>
                                        );
                                        })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Arma */}
                    {equippedSkin?.type !== 'Arquero' && unlockedArmes.length > 0 && (
                        <ItemPicker
                            type="arma" label="⚔️ Espada" icon="⚔️"
                            items={allSkins.armes?.filter((a: any) => {
                                if (a.active !== 1 || !unlockedArmes.includes(a.id)) return false;
                                return true;
                            }) || []}
                            equippedId={userEquipment.id_arma}
                            groupByEvolution={true}
                        />
                    )}

                    {/* Arco */}
                    {equippedSkin?.type === 'Arquero' && unlockedArcs.length > 0 && (
                        <ItemPicker
                            type="arc" label="🏹 Arco" icon="🏹"
                            items={allSkins.arcs?.filter((a: any) => a.active === 1 && unlockedArcs.includes(a.id)) || []}
                            equippedId={userEquipment.id_arc}
                            groupByEvolution={true}
                        />
                    )}
                    
                    {/* Flecha */}
                    {equippedSkin?.type === 'Arquero' && unlockedFlechas.length > 0 && (
                        <ItemPicker
                            type="flecha" label="🎯 Flechas" icon="🎯"
                            items={allSkins.flechas?.filter((f: any) => f.active === 1 && unlockedFlechas.includes(f.id)) || []}
                            equippedId={userEquipment.id_flecha}
                            disabled={!equippedArc}
                            disabledMessage={!equippedArc ? "Equipa un arco para usar flechas" : undefined}
                        />
                    )}
                    </div>
                    </Col>
                </Row>

                {savingEquipment && (
                    <div style={{ textAlign: 'center', marginTop: 12, color: '#999', fontSize: 13 }}>
                        Guardando...
                    </div>
                )}
            </Card>
        );
    };

    if (isRpgOnly) {
        return (
            <div style={{ padding: 16, background: '#f0f2f5', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <div style={{ maxWidth: 1100, width: '100%', margin: '0 auto' }}>
                    {renderRpgEquipment()}
                </div>
            </div>
        );
    }

    return (
        <Content style={{ padding: "0 24px", minHeight: 280 }}>
            <div style={{ maxWidth: 1100, margin: '0 auto', paddingTop: 20, paddingBottom: 40 }}>

                {/* ===== HEADER CARD ===== */}
                <Card style={{ ...sectionCardStyle, overflow: 'visible' }} bodyStyle={{ padding: 0 }}>
                    <div style={gradientHeader}>
                        {/* Decorative circles */}
                        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: -60, right: -40 }} />
                        <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', bottom: -30, left: 60 }} />

                        <div style={{ display: 'flex', alignItems: 'center', gap: 28, position: 'relative', zIndex: 1 }}>
                            <div style={{ position: 'relative' }}>
                                <img
                                    src={currentAvatar}
                                    alt="avatar"
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).src = getDefaultAvatarImage();
                                    }}
                                    style={{
                                        width: 110,
                                        height: 110,
                                        borderRadius: '50%',
                                        border: '4px solid rgba(255,255,255,0.8)',
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    bottom: 4,
                                    right: 4,
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    background: '#52c41a',
                                    border: '3px solid #fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <CheckCircleFilled style={{ color: '#fff', fontSize: 14 }} />
                                </div>
                            </div>
                            <div>
                                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#fff', letterSpacing: '-0.5px' }}>
                                    {alumne.alumne.nom} {alumne.alumne.cognoms}
                                </h1>
                                <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.75)', fontSize: 15 }}>
                                    {alumne.alumne.email || 'Sin email configurado'}
                                </p>
                                {hasJutgePermission && (
                                    <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                                        <Tag style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 20, padding: '2px 14px' }}>
                                            <StarFilled style={{ marginRight: 4 }} />{totalPoints} puntos
                                        </Tag>
                                        <Tag style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 20, padding: '2px 14px' }}>
                                            <TrophyFilled style={{ marginRight: 4 }} />{misTrofeos.length} trofeos
                                        </Tag>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ===== FORM ===== */}
                    <div style={{ padding: '28px 40px 20px' }}>
                        <Form form={form} onFinish={submitData} layout="vertical">
                            <Row gutter={24}>
                                <Col span={12}>
                                    <Form.Item label="ID" name="id">
                                        <Input disabled style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                    <Form.Item label="Nombre" name="nom" rules={[{ required: true }]}>
                                        <Input style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                    <Form.Item label="Apellidos" name="cognoms" rules={[{ required: true }]}>
                                        <Input style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Email" name="email">
                                        <Input style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                    <Form.Item label="GitLab" name="gitlab">
                                        <Input style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                    <Form.Item label="Curs" name="curs" rules={[{ required: true }]}>
                                        <Cascader options={cursos} placeholder="Selecciona cicle" style={{ borderRadius: 8 }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* AVATAR SELECTION */}
                            <Divider style={{ margin: '8px 0 20px' }} />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#333' }}>
                                        <UserOutlined style={{ marginRight: 8, color: '#764ba2' }} />
                                        Elige tu avatar
                                    </h3>
                                    <p style={{ margin: '2px 0 0', color: '#999', fontSize: 13 }}>Selecciona entre los avatares desbloqueados o los generados con IA</p>
                                </div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {user?.tipus !== "1" && user?.tipus !== "0" && (
                                        <Button type="default" size="small" onClick={async () => {
                                            const res = await import("../../../dao/jutge/PostSugeriment").then(m => m.PostSugeriment(userId, "SOLICITUD_ROL_PROFESOR", false));
                                            if (res) alert("Solicitud enviada correctamente.");
                                            else alert("Error al enviar solicitud.");
                                        }}>
                                            Solicitar Rol Profesor
                                        </Button>
                                    )}

                                    {hasAvatarGeneratorItem && (
                                        <Button
                                            type="primary"
                                            onClick={openAvatarModal}
                                            style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', borderRadius: 8 }}
                                            icon={<ThunderboltOutlined />}
                                        >
                                            Crear Avatar IA
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* DB Avatars (Unlocked) */}
                            {dbAvatars.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: 13, color: '#888', marginBottom: 8, fontWeight: 500 }}>
                                        <CrownOutlined style={{ marginRight: 4 }} /> Avatares del juego
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                        {dbAvatars
                                            .filter((avatar: any) => avatar.id === 1 || unlockedAvatarIds.includes(avatar.id))
                                            .map((avatar: any) => {
                                                const imgSrc = getAvatarImageSrc(avatar);
                                                // Está seleccionado si: es el id activo, o la URL guardada apunta a este avatar
                                                const dbUrl = getDbAvatarUrl(avatar.id);
                                                const isSelected = Boolean(selectedDbAvatarId === avatar.id
                                                    || currentAvatar === imgSrc
                                                    || currentAvatar === dbUrl
                                                    || (avatar.id === 1 && (currentAvatar === '/images/avatar1.jpg' || currentAvatar === 'avatar1.jpg'))
                                                    || (currentAvatar && (currentAvatar.endsWith(`/jutge/avatar-content/${avatar.id}`) || currentAvatar.endsWith(`/jutge/game/image/avatar/${avatar.id}`))));
                                                return (
                                                    <Tooltip key={avatar.id} title={avatar.nom}>
                                                        <div
                                                            onClick={() => handleDbAvatarClick(avatar)}
                                                            style={{ position: 'relative' }}
                                                        >
                                                            <img
                                                                src={imgSrc}
                                                                alt={avatar.nom}
                                                                style={avatarRingStyle(isSelected)}
                                                            />
                                                            {isSelected && (
                                                                <CheckCircleFilled style={{
                                                                    position: 'absolute', bottom: 2, right: 2,
                                                                    color: '#764ba2', fontSize: 18,
                                                                    background: '#fff', borderRadius: '50%'
                                                                }} />
                                                            )}
                                                        </div>
                                                    </Tooltip>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}

                            {/* AI Generated Avatars */}
                            {customAvatars.length > 0 && (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: 13, color: '#888', marginBottom: 8, fontWeight: 500 }}>
                                        <ThunderboltOutlined style={{ marginRight: 4 }} /> Avatares generados con IA
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                        {customAvatars.map((img, idx) => {
                                            const isSelected = currentAvatar === img;
                                            return (
                                                <Tooltip key={idx} title={`Avatar IA #${idx + 1}`}>
                                                    <div
                                                        onClick={() => handleImageClick(img)}
                                                        style={{ position: 'relative' }}
                                                    >
                                                        <img
                                                            src={img}
                                                            alt={`ai-avatar-${idx}`}
                                                            style={avatarRingStyle(isSelected)}
                                                        />
                                                        {isSelected && (
                                                            <CheckCircleFilled style={{
                                                                position: 'absolute', bottom: 2, right: 2,
                                                                color: '#764ba2', fontSize: 18,
                                                                background: '#fff', borderRadius: '50%'
                                                            }} />
                                                        )}
                                                    </div>
                                                </Tooltip>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <Divider style={{ margin: '16px 0' }} />
                            <Form.Item>
                                <Button
                                    htmlType="submit"
                                    loading={enviado}
                                    disabled={enviado}
                                    size="large"
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 10,
                                        fontWeight: 600,
                                        padding: '0 40px',
                                        height: 44,
                                        boxShadow: '0 4px 14px rgba(118,75,162,0.3)',
                                    }}
                                >
                                    Guardar cambios
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </Card>

                {/* ===== IA AVATAR MODAL ===== */}
                <Modal
                    title={<span><ThunderboltOutlined style={{ color: '#764ba2', marginRight: 8 }} />Generar Avatar Personalizado (IA)</span>}
                    open={isAvatarModalVisible}
                    onCancel={handleSaveAndClose}
                    maskClosable={false}
                    footer={null}
                    styles={{ body: { padding: '20px 24px' } }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                        {!generatedAvatar ? (
                            <>
                                <p style={{ color: '#666' }}>Configura los parámetros base y añade accesorios personalizados:</p>

                                <Form layout="vertical">
                                    <Form.Item label="Género" style={{ marginBottom: 15 }}>
                                        <Radio.Group
                                            value={avatarGender}
                                            onChange={(e) => setAvatarGender(e.target.value)}
                                            buttonStyle="solid"
                                        >
                                            <Radio.Button value="man">Hombre</Radio.Button>
                                            <Radio.Button value="woman">Mujer</Radio.Button>
                                            <Radio.Button value="non-binary">No Binario</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>

                                    {renderOptionSelector("Fondo", BACKGROUND_COLORS, avatarBgId, setAvatarBgId)}
                                    {renderOptionSelector("Tono de Piel", SKIN_OPTIONS, avatarSkinId, setAvatarSkinId)}
                                    {renderOptionSelector("Color de Pelo", HAIR_COLORS, avatarHairId, setAvatarHairId)}
                                </Form>

                                <div style={{ marginTop: 10 }}>
                                    <p style={{ marginBottom: 5, fontWeight: 500 }}>Accesorios y detalles:</p>
                                    <Input.TextArea
                                        rows={2}
                                        placeholder="Ej: Gafas de sol, auriculares, barba, bufanda..."
                                        value={avatarPrompt}
                                        onChange={(e) => setAvatarPrompt(e.target.value)}
                                    />
                                </div>

                                <Button
                                    type="primary"
                                    htmlType="button"
                                    loading={generating}
                                    onClick={() => handleGenerateAvatar(false)}
                                    style={{ alignSelf: 'flex-end', background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', width: '100%', marginTop: 10, height: 42, borderRadius: 8, fontWeight: 600 }}
                                >
                                    {generating ? "Generando..." : "Generar con IA"}
                                </Button>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: 20, borderRadius: 12, background: '#fafafa' }}>
                                <h5 style={{ color: '#333' }}>Vista Previa:</h5>
                                <img src={generatedAvatar} alt="Generated" style={{ width: 150, height: 150, borderRadius: '50%', marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                                    <Button
                                        type="primary"
                                        onClick={handleSaveAndClose}
                                        style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', borderRadius: 8, fontWeight: 600 }}
                                    >
                                        Guardar en Galería
                                    </Button>

                                    {canRetry && (
                                        <Button
                                            danger
                                            loading={generating}
                                            onClick={() => handleGenerateAvatar(true)}
                                            style={{ borderRadius: 8 }}
                                        >
                                            Reintentar (1/1)
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>

                {/* ===== EQUIPAMIENTO RPG ===== */}
                {renderRpgEquipment()}

                {/* ===== PUNTOS ===== */}
                {hasJutgePermission && (
                    <Card style={sectionCardStyle} bodyStyle={{ padding: '24px 32px' }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#333' }}>
                                    <StarFilled style={{ color: '#faad14', marginRight: 8 }} />
                                    Puntos totales: <span style={{ color: '#764ba2' }}>{totalPoints}</span>
                                </h3>
                            </div>
                            <Button
                                type="primary"
                                icon={<ShoppingOutlined />}
                                onClick={() => navigate("/home/javashop")}
                                size="large"
                                style={{
                                    background: 'linear-gradient(135deg, #f7971e, #ffd200)',
                                    border: 'none',
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    color: '#333',
                                    boxShadow: '0 4px 14px rgba(247,151,30,0.3)',
                                }}
                            >
                                Ir a JAVATienda
                            </Button>
                        </div>
                        <Row gutter={[16, 16]}>
                            {aluPoints.length === 0 ? (
                                <Col span={24} style={{ color: "#999" }}>Aún no tienes puntos.</Col>
                            ) : (
                                aluPoints.map((p: any) => (
                                    <Col key={p.id} xs={24} sm={12} md={8} lg={6}>
                                        <Card
                                            size="small"
                                            style={{
                                                borderRadius: 12,
                                                background: p.color ? `${p.color}10` : '#f9f9f9',
                                                border: `1px solid ${p.color || '#eee'}22`,
                                                transition: 'transform 0.2s',
                                            }}
                                            bodyStyle={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                padding: "10px 12px",
                                                width: '100%',
                                                overflow: 'hidden'
                                            }}
                                            hoverable
                                        >
                                            {getIcon(p.color)}
                                            <span style={{ fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.descripcio}>{p.descripcio}</span>
                                            <span style={{ marginLeft: "auto", fontWeight: "bold", color: p.color, fontSize: 15 }}>{p.punts}</span>
                                        </Card>
                                    </Col>
                                ))
                            )}
                        </Row>
                    </Card>
                )}

                {/* ===== TROFEOS ===== */}
                {hasJutgePermission && (
                    <Card style={sectionCardStyle} bodyStyle={{ padding: '24px 32px' }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#333' }}>
                                <TrophyFilled style={{ color: '#faad14', marginRight: 8 }} />
                                Mis Trofeos <Tag color="purple" style={{ marginLeft: 8, borderRadius: 20 }}>{misTrofeos.length}</Tag>
                            </h3>
                        </div>
                        <Row gutter={[16, 16]}>
                            {misTrofeos.length === 0 ? (
                                <Col span={24} style={{ color: "#999" }}>Aún no has ganado ningún trofeo.</Col>
                            ) : (
                                misTrofeos.map((t: any) => (
                                    <Col key={t.id} xs={24} sm={12} md={8} lg={6}>
                                        <Card
                                            hoverable
                                            style={{
                                                borderRadius: 14,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                textAlign: "center",
                                                background: t.color ? `${t.color}08` : "#fafafa",
                                                border: `2px solid ${t.color || '#eee'}`,
                                                transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
                                            }}
                                            bodyStyle={{ padding: 20, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                                        >
                                            {t.imagen ? (
                                                <img src={`data:image/png;base64,${t.imagen}`} alt={t.nom} style={{ width: 60, height: 60, objectFit: 'contain', marginBottom: 12 }} />
                                            ) : (
                                                <TrophyFilled style={{ fontSize: 48, color: t.color || '#faad14', marginBottom: 12 }} />
                                            )}
                                            <h5 style={{ margin: '0 0 8px 0', color: '#333', fontWeight: 600 }}>{t.nom}</h5>
                                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>{t.descr}</p>
                                        </Card>
                                    </Col>
                                ))
                            )}
                        </Row>
                    </Card>
                )}
            </div>
        </Content>
    );
};

export default Perfil;
